/**
 * Inferência de tratamento/anúncio — ESPELHO de `functions/messaging/inferencia.js`
 * para a tela de Captação (validação das frases e prévia das regras padrão).
 *
 * Duplicado de propósito, como `telefone.js` e `PAPEIS_NUMERO`: o deploy das
 * Functions empacota só `functions/`, então os dois lados não podem importar um
 * do outro. Quem alterar `normalizar`/`normalizarFrase`/`casarCaptacao`/`inferir`/
 * `REGRAS_PADRAO` lá, altera aqui — `inferencia.espelho.test.js` importa os dois.
 *
 * O que só existe aqui: os auxiliares da tela (frases repetidas e contidas).
 * ★ PC-7: o gerador de links `wa.me` SAIU — o dono não usa aqueles links.
 */
import { PROCEDIMENTOS_PADRAO } from '@/lib/constants';

/**
 * Regras padrão. A ORDEM é a prioridade: a primeira que casa vence, então as
 * específicas vêm antes das genéricas ("protocolo" antes de "protese"; "avaliacao"
 * por último, porque quase toda mensagem de anúncio menciona consulta). "Outro"
 * não entra — não há palavra que o identifique.
 *
 * As palavras já estão na forma normalizada (sem acento, minúsculas), mas
 * `inferir` normaliza de novo: a clínica digita com acento na tela.
 */
export const REGRAS_PADRAO = [
  { palavras: ['implante', 'implantes', 'implantar'], procedimento: 'Implante', anuncio: null },
  { palavras: ['protocolo', 'protese fixa', 'all on'], procedimento: 'Protocolo', anuncio: null },
  { palavras: ['ortodontia', 'aparelho', 'alinhador', 'invisalign'], procedimento: 'Ortodontia', anuncio: null },
  { palavras: ['lente', 'lentes', 'faceta', 'facetas', 'estetica'], procedimento: 'Estética / Lentes', anuncio: null },
  { palavras: ['protese', 'dentadura'], procedimento: 'Prótese', anuncio: null },
  { palavras: ['clareamento', 'clarear', 'branquear'], procedimento: 'Clareamento', anuncio: null },
  { palavras: ['avaliacao', 'avaliar', 'consulta'], procedimento: 'Avaliação', anuncio: null },
];

/** Sem acento, minúsculas, espaços colapsados. Vazio para nulo/não-string. */
export function normalizar(texto) {
  if (typeof texto !== 'string') return '';
  return texto
    .normalize('NFD')
    .replace(/\p{M}+/gu, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

const escapar = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * A palavra casa por INÍCIO DE PALAVRA no texto normalizado: "implante" casa
 * "implantes" e "implante dentario", mas "dente" não casa "aparente". Palavras
 * compostas ("protese fixa") casam como sequência.
 */
export function palavraCasa(palavra, textoNormalizado) {
  const p = normalizar(palavra);
  if (!p) return false;
  return new RegExp(`(^|[^a-z0-9])${escapar(p)}`).test(textoNormalizado);
}

// ---------------------------------------------------------------- ★ PC-7: FRASE PRONTA

/**
 * ★★ PC-7 (25/out/2026) — A FRASE PRONTA DO ANÚNCIO.
 *
 * O anúncio põe uma pergunta pronta na boca da pessoa ("Olá, vim das redes sociais e gostaria de
 * mais informações sobre implantes"). O gestor cadastra essa frase numa regra da Captação, com a
 * ORIGEM, o NOME do anúncio e (opcional) o PROCEDIMENTO. Medido em Campinas (60 d, 996 1ªs mensagens):
 * 6 frases de anúncio cobrem 543.
 *
 * ★ Casa quando a 1ª mensagem CONTÉM a frase, por PALAVRA INTEIRA, com os dois lados em
 * `normalizarFrase` (sem acento, minúsculas, SEM PONTUAÇÃO). ⚠⚠ Sem tirar a pontuação, nenhuma das
 * 543 casaria: a frase real chega com "Olá," e vírgula (mutação medida na simulação do plano).
 *
 * ★★ Havendo várias, vence a MAIS LONGA — a frase da avaliação ESTÁ DENTRO da frase do aparelho
 * (F5 ⊂ F4, 79 leads); empate de tamanho → a regra de cima. Aproximação (fuzzy) está proibida pelo
 * dado: implante × protocolo e avaliação × aparelho ficam a 10–25 % de distância.
 *
 * ⚠ O código ` #XX` da VH SAIU: zero uso medido em 60 dias, nas 4 clínicas. Regra antiga com `codigo`
 * ou `textoLink` continua válida pelas palavras; os dois campos são ignorados.
 */

/** Minúsculas, sem acento, SEM PONTUAÇÃO (o que não é letra/dígito/espaço vira espaço), espaços únicos. */
export function normalizarFrase(texto) {
  if (typeof texto !== 'string') return '';
  return texto
    .normalize('NFD')
    .replace(/\p{M}+/gu, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Palavras de cumprimento (já normalizadas). ★ D2 do dono: frase feita SÓ delas não é frase de
 * anúncio — os 11 cumprimentos com 5+ leads em Campinas ("bom dia" 56, "boa tarde" 43, "oi" 38…)
 * são todos assim, e cadastrar um deles daria origem a quem não veio de anúncio nenhum.
 */
export const PALAVRAS_DE_CUMPRIMENTO = [
  'oi', 'oii', 'ola', 'oie', 'opa', 'alo', 'bom', 'boa', 'dia', 'dias', 'tarde', 'tardes', 'noite', 'noites',
  'tudo', 'bem', 'td', 'blz', 'beleza', 'ok', 'okay', 'sim', 'e', 'ai', 'como', 'vai', 'voce', 'vc', 'esta',
];
const CUMPRIMENTO = new Set(PALAVRAS_DE_CUMPRIMENTO);

/** Mínimo de palavras de uma frase, DEPOIS de normalizar (D2). */
export const MINIMO_PALAVRAS_FRASE = 3;

/**
 * Por que a frase não serve, ou `null` quando serve. A tela mostra o motivo; o servidor só pergunta
 * se é `null` (`fraseValida`) e IGNORA a inválida — a mesma regra nos dois lados (D2).
 */
export function motivoFraseInvalida(frase) {
  const f = normalizarFrase(frase);
  if (!f) return 'vazia';
  const palavras = f.split(' ');
  if (palavras.length < MINIMO_PALAVRAS_FRASE) return 'curta';
  if (palavras.every((p) => CUMPRIMENTO.has(p))) return 'cumprimento';
  return null;
}
export const fraseValida = (frase) => motivoFraseInvalida(frase) === null;

/** As frases UTILIZÁVEIS da regra, normalizadas. Frase inválida (D2) não entra. */
export function frasesDaRegra(regra) {
  return (Array.isArray(regra?.frases) ? regra.frases : [])
    .filter((f) => typeof f === 'string' && fraseValida(f))
    .map(normalizarFrase);
}

/** A frase (normalizada) está no texto (normalizado) como sequência de PALAVRAS INTEIRAS. */
export function fraseCasa(fraseNormalizada, textoNormalizado) {
  if (!fraseNormalizada || !textoNormalizado) return false;
  return ` ${textoNormalizado} `.includes(` ${fraseNormalizada} `);
}

const texto_ = (v) => (typeof v === 'string' ? v.trim() : '');
const temPalavras = (regra) => Array.isArray(regra?.palavras) && regra.palavras.some((p) => normalizar(p));

/**
 * Regra utilizável: tem FRASE válida, OU tem palavra E procedimento (a regra da V3).
 * ★ PC-7: o procedimento deixou de ser obrigatório para a regra de frase (Google Meu Negócio,
 * frase genérica da Meta — origem e nome sem tema).
 */
export function regraValida(regra) {
  return Boolean(regra) && (frasesDaRegra(regra).length > 0 || (temPalavras(regra) && Boolean(texto_(regra.procedimento))));
}

/**
 * ★ As duas passadas, sobre as regras VÁLIDAS:
 *   1. FRASE — entre TODAS as frases de TODAS as regras, a MAIS LONGA contida vence; empate → a de cima;
 *   2. PALAVRA — a primeira regra (ordem da lista) com palavra E procedimento cuja palavra casa por
 *      início de palavra (`palavraCasa`, inalterada). ★ D1: a palavra serve SÓ para procedimento.
 *
 * @returns `{ frase: { regra, frase } | null, palavra: { regra } | null }`
 */
export function casarCaptacao(texto, regras = REGRAS_PADRAO) {
  const validas = (Array.isArray(regras) ? regras : []).filter(regraValida);
  const tf = normalizarFrase(texto);
  let frase = null;
  if (tf) {
    for (const regra of validas) {
      for (const f of frasesDaRegra(regra)) {
        // `>` estrito: no empate fica a que já estava — a da regra de cima.
        if (fraseCasa(f, tf) && (!frase || f.length > frase.frase.length)) frase = { regra, frase: f };
      }
    }
  }
  const tp = normalizar(texto);
  let palavra = null;
  if (tp) {
    for (const regra of validas) {
      if (texto_(regra.procedimento) && temPalavras(regra) && regra.palavras.some((p) => palavraCasa(p, tp))) {
        palavra = { regra };
        break;
      }
    }
  }
  return { frase, palavra };
}

/**
 * O que a Captação dá ao lead: `{ procedimento, anuncio }` (forma da V3, mantida).
 *
 *   procedimento = o da regra da FRASE, senão o da regra da PALAVRA, senão '';
 *   anuncio      = o NOME da regra da FRASE, ou null. ★ PC-7: palavra nunca dá nome de anúncio.
 *
 * `null` quando nada casou, ou quando casou só uma frase sem procedimento e sem nome (aí ela só
 * dá a origem, que é decidida em `origens.js`).
 */
export function inferir(texto, regras = REGRAS_PADRAO) {
  const { frase, palavra } = casarCaptacao(texto, regras);
  const procedimento = texto_(frase?.regra.procedimento) || texto_(palavra?.regra.procedimento);
  const anuncio = texto_(frase?.regra.anuncio) || null;
  if (!procedimento && !anuncio) return null;
  return { procedimento, anuncio };
}

/** Origem da regra (id de `config.origens`) ou '' — as regras antigas não têm o campo. */
export const origemDaRegra = (regra) => texto_(regra?.origem);

/**
 * Regras efetivas de uma clínica: as configuradas, quando há pelo menos uma
 * válida; senão as padrão. Lista vazia ou malformada NÃO desliga a inferência:
 * cai no padrão. Desligar de vez não é opção desta peça — se um dia for, será um
 * campo explícito na config, não uma lista vazia que pode ser acidente.
 */
export function regrasDaClinica(config) {
  const lista = Array.isArray(config?.inferencia) ? config.inferencia.filter(regraValida) : [];
  return lista.length ? lista : REGRAS_PADRAO;
}

/** Procedimentos que a tela oferece: os da clínica, senão os padrão. */
export const procedimentosDaClinica = (config) =>
  (Array.isArray(config?.procedimentos) && config.procedimentos.length
    ? config.procedimentos
    : PROCEDIMENTOS_PADRAO);

/** Converte "implante, implantes , implantar" em lista limpa. */
export const palavrasDoTexto = (texto) =>
  String(texto ?? '').split(',').map((p) => p.trim()).filter(Boolean);

/** Uma frase por LINHA (frase tem vírgula — não dá para separar por vírgula). */
export const frasesDoTexto = (texto) =>
  String(texto ?? '').split('\n').map((f) => f.trim()).filter(Boolean);

// ---------------------------------------------------------------- ★ PC-7: validação da aba

/**
 * Frases (normalizadas) que aparecem em MAIS DE UMA regra, ou duas vezes na mesma. A aba recusa
 * salvar: com a mesma frase em duas regras, quem decide é a ordem, e o gestor não vê isso.
 */
export function frasesRepetidas(regras) {
  const contagem = new Map();
  for (const r of Array.isArray(regras) ? regras : []) {
    for (const f of (Array.isArray(r?.frases) ? r.frases : []).map(normalizarFrase).filter(Boolean)) {
      contagem.set(f, (contagem.get(f) ?? 0) + 1);
    }
  }
  return [...contagem.entries()].filter(([, n]) => n > 1).map(([f]) => f);
}

/**
 * Pares em que uma frase ESTÁ DENTRO de outra (palavra inteira) — F5 ⊂ F4 no dado de Campinas. A aba
 * só AVISA: é legítimo (a mais longa vence), mas o gestor precisa saber para onde cada uma vai.
 *
 * @returns `[{ curta, longa, regraCurta, regraLonga }]` (índices das regras, base 0)
 */
export function frasesContidas(regras) {
  const todas = [];
  (Array.isArray(regras) ? regras : []).forEach((r, i) => {
    for (const f of (Array.isArray(r?.frases) ? r.frases : []).map(normalizarFrase).filter(Boolean)) todas.push({ f, i });
  });
  const pares = [];
  for (const a of todas) {
    for (const b of todas) {
      if (a.f.length < b.f.length && fraseCasa(a.f, b.f)) {
        pares.push({ curta: a.f, longa: b.f, regraCurta: a.i, regraLonga: b.i });
      }
    }
  }
  return pares;
}
